import React, { useEffect, useRef, useState } from 'react';

import styles from './country_filter.module.css';

const summaryLabel = (selectedCountries) => {
  if (selectedCountries.length === 0) {
    return 'All countries';
  }

  if (selectedCountries.length === 1) {
    return selectedCountries[0];
  }

  return `${selectedCountries.length} countries`;
};

// A multi-select rather than a single-country dropdown, because a program manager usually compares
// a subset of countries. Only the countries represented by the viewer's granted sites are offered,
// so the filter can never reach past what the metric services already allow.
export default function CountryFilter({ countries, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const toggle = (country) => {
    if (selected.includes(country)) {
      onChange(selected.filter((value) => value !== country));
      return;
    }

    onChange([...selected, country]);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <span className={styles.label} id='program-insights-country-filter-label'>
        Country
      </span>
      <button
        aria-expanded={open}
        aria-haspopup='true'
        aria-labelledby='program-insights-country-filter-label program-insights-country-filter-summary'
        className={selected.length > 0 ? `${styles.trigger} ${styles.active}` : styles.trigger}
        onClick={() => setOpen(!open)}
        type='button'
      >
        <span id='program-insights-country-filter-summary'>{summaryLabel(selected)}</span>
        <svg aria-hidden='true' className={styles.caret} viewBox='0 0 12 12'>
          <path d='M2.5 4.5L6 8L9.5 4.5' fill='none' stroke='currentColor' strokeLinecap='round' strokeWidth='1.5' />
        </svg>
      </button>

      {open && (
        <div aria-label='Countries' className={styles.popover} role='group'>
          {countries.length === 0 && <p className={styles.empty}>No countries are available for your sites.</p>}

          {countries.map((country) => (
            <label className={styles.option} key={country}>
              <input checked={selected.includes(country)} onChange={() => toggle(country)} type='checkbox' />
              <span className={styles.option_name}>{country}</span>
            </label>
          ))}

          {selected.length > 0 && (
            <button className={styles.clear} onClick={() => onChange([])} type='button'>
              Show all countries
            </button>
          )}
        </div>
      )}
    </div>
  );
}
